import { postData } from "../Services/postData";
import { ClientModel } from "./Models";
import { Subscription } from "./Types";
import { endpoints, localUrlEnum } from "./UrlEnum";
import { Vocabulary } from "./Vocabulary";
import { normalizePhone, removeDiacritics } from "./Utils";
import { SKIP_PAYMENT_IF_ZERO_AMOUNT } from "./Constants";
import ShortUniqueId from "short-unique-id";

interface PaymentGenerationParams {
  clientId?: string;
  clientData: ClientModel;
  selectedSubscription?: Subscription | null;
  amount?: number;
  orderDescription?: string;
  saveOrderId?: boolean;
}

/**
 * Generates a payment URL and redirects to the payment page
 * Stores necessary data in localStorage for post-payment processing
 *
 * Can be used for both subscription payments and product order payments
 * For logged-in users, returns to /client-account with modal
 * For unlogged-in users, returns to /payment-status page
 *
 * If SKIP_PAYMENT_IF_ZERO_AMOUNT is enabled and amount is 0:
 * - Returns { paymentSkipped: true, redirectUrl: '...' }
 * - The calling component should show a modal and then redirect
 * - Does NOT automatically redirect
 *
 * @param params - Object containing:
 *   - clientId: Optional client ID to store in localStorage
 *   - clientData: Client personal and address information
 *   - selectedSubscription: Optional subscription object (for subscription payments)
 *   - amount: Optional custom amount (for non-subscription payments)
 *   - orderDescription: Optional custom order description
 *   - saveOrderId: Whether to save the returned orderId (default: false)
 * @returns PaymentGenerationResult with paymentSkipped flag and optional redirectUrl
 */
export const generatePaymentUrl = async (params: PaymentGenerationParams) => {
  const {
    clientId,
    clientData,
    selectedSubscription,
    amount: customAmount,
    orderDescription: customDescription,
  } = params;

  if (!selectedSubscription && customAmount == undefined) {
    console.error(
      "Either selectedSubscription or customAmount must be provided",
    );
    return { paymentSkipped: false };
  }

  try {
    // Store client ID for later use (if provided)
    if (clientId) {
      localStorage.setItem("processingClientId", clientId);
    }

    if (selectedSubscription) {
      localStorage.setItem("subscriptionId", selectedSubscription._id);
    }

    // Determine the amount to use
    const amount = customAmount || selectedSubscription?.price;
    localStorage.setItem("paymentAmount", amount?.toString() || "");

    /**
     * FEATURE FLAG: Skip payment if amount is 0
     * If the feature is enabled and amount is 0, return without redirecting
     * The calling component will handle showing a modal and redirecting
     */
    if (SKIP_PAYMENT_IF_ZERO_AMOUNT && !amount) {
      const isLoggedIn = !!localStorage.getItem("clientId");
      const redirectUrl = isLoggedIn
        ? `${window.location.origin}${localUrlEnum.clientAccount}?paymentComplete=true`
        : `${window.location.origin}${localUrlEnum.login}`;

      return { paymentSkipped: true, redirectUrl };
    }

    // Prepare address info
    const addressInfo = {
      country: "642", // Romania
      city: removeDiacritics(clientData?.address?.city || ""),
      postAddress: removeDiacritics(
        clientData?.address?.country + ", " + clientData?.address?.city,
      ),
    };

    console.log('removed diacritics')
    // Prepare order details
    const orderDate = new Date();
    const order = {
      orderCreationDate: orderDate.toISOString().split("T")[0],
      customerDetails: {
        email: clientData.email,
        phone: Number(normalizePhone(clientData.phone)),
        deliveryInfo: {
          deliveryType: Vocabulary.advance,
          ...addressInfo,
        },
        billingInfo: {
          ...addressInfo,
        },
      },
    };

    // Generate unique order number
    const { randomUUID } = new ShortUniqueId({ length: 30 });

    // Build payment parameters
    let orderDescription =
      customDescription ||
      (selectedSubscription
        ? `${Vocabulary.subscription} ${selectedSubscription.name}`
        : Vocabulary.advance);
    orderDescription = removeDiacritics(orderDescription);

    // Determine return URL based on login status
    // If user has a clientId in localStorage, they are logged in
    const isLoggedIn = !!localStorage.getItem("clientId");
    const returnUrl = isLoggedIn
      ? `${window.location.origin}${localUrlEnum.clientAccount}?paymentComplete=true`
      : `${window.location.origin}${localUrlEnum.paymentStatus}`;

    const paymentParams = new URLSearchParams({
      orderNumber: randomUUID().toString(),
      amount: amount ? (amount * 100).toString() : "",
      currency: "946", // RON
      returnUrl,
      orderBundle: JSON.stringify(order),
      orderDescription,
    });

    // Request payment URL
    const response = await postData(
      endpoints.payments.generatePaymentUrl,
      paymentParams,
    );

    if (response?.data?.formUrl) {
      // Save order ID if requested
      if (response?.data?.orderId) {
        localStorage.setItem("paymentOrderId", response.data.orderId);
      }
      // Redirect to payment page
      window.location.href = response.data.formUrl;
    } else {
      console.error("No payment form URL received from server");
    }
  } catch (error) {
    console.error("Error generating payment URL:", error);
  }
};
